import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import CloseIcon from '@mui/icons-material/Close';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

/**
 * Presentation pieces for the completed-quote view.
 *
 * Split out of PricingPopup, which is already long. Everything here is derived
 * from data that is in client state by the time the popup opens — no fetches.
 */

const money = (n) =>
  `$${Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

/* ------------------------------------------------------------------ images */

/* Two equal tracks — the design and the sun map are the same size, so neither
   reads as an afterthought and nothing reflows when the second one lands. The
   status line spans both columns so it sits centred beneath the pair. */
const ShotsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: 10px;
  row-gap: 8px;
  width: 100%;
  max-width: 420px;
  margin: 0 auto;
`;

const Figure = styled.figure`
  margin: 0;
  min-width: 0;
`;

const Frame = styled.div`
  border-radius: 10px;
  border: 1px solid #e0e6ea;
  overflow: hidden;
  aspect-ratio: 1 / 1;
  background: #f3f6f8;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(15, 27, 36, 0.08);
`;

const Shot = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

/* Reserves the frame's space while a capture is still uploading, so nothing
   reflows when it lands. Each frame animates independently — they arrive
   seconds apart, and a still placeholder next to a moving one reads as broken. */
const Shimmer = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  background: linear-gradient(100deg, #eef3f6 30%, #dfeaf2 50%, #eef3f6 70%);
  background-size: 220% 100%;
  animation: shimmerSweep 1.5s linear infinite;
  /* Offset the second frame so the two sweeps aren't in lockstep. */
  animation-delay: ${({ $delay }) => $delay || '0s'};

  @keyframes shimmerSweep {
    from { background-position: 180% 0; }
    to { background-position: -60% 0; }
  }

  /* Faint grid, echoing the scan on the loading screen so the two states
     read as the same system. */
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background-image: linear-gradient(rgba(27, 168, 241, 0.08) 1px, transparent 1px),
      linear-gradient(90deg, rgba(27, 168, 241, 0.08) 1px, transparent 1px);
    background-size: 18px 18px;
  }

  @media (prefers-reduced-motion: reduce) { animation: none; }
`;

const Caption = styled.figcaption`
  font-size: 10.5px;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  font-weight: 700;
  color: #808080;
  margin-top: 5px;
  text-align: center;
  /* Labels fade in with their image rather than sitting over an empty frame. */
  animation: captionIn 420ms ease both;
  @keyframes captionIn {
    from { opacity: 0; transform: translateY(-2px); }
    to { opacity: 1; transform: none; }
  }
  @media (prefers-reduced-motion: reduce) { animation: none; }
`;

/* The whole frame is the hit target, not a small icon: on a phone these images
   are ~150px wide and the detail the customer wants — where the panels sit, how
   the roof shades — is not legible until it fills the screen. */
const ZoomTarget = styled.button`
  all: unset;
  box-sizing: border-box;
  display: block;
  position: relative;
  width: 100%;
  height: 100%;
  cursor: zoom-in;

  &:focus-visible {
    outline: 2px solid #1BA8F1;
    outline-offset: -2px;
  }
`;

/* Always visible rather than on hover: most of this flow is on a phone, where
   hover never happens and an invisible affordance is no affordance. */
const ZoomHint = styled.span`
  position: absolute;
  right: 6px;
  bottom: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 6px;
  background: rgba(15, 27, 36, 0.55);
  color: #fff;
  pointer-events: none;

  svg {
    font-size: 13px;
  }

  ${ZoomTarget}:hover & {
    background: rgba(15, 27, 36, 0.78);
  }
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1300;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 24px;
  box-sizing: border-box;
  background: rgba(9, 17, 23, 0.92);
  animation: lightboxIn 160ms ease both;

  @keyframes lightboxIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @media (prefers-reduced-motion: reduce) { animation: none; }
`;

/* `contain`, deliberately — the thumbnails crop to a square, and seeing the
   whole capture uncropped is most of the reason to open this at all. */
const BigShot = styled.img`
  max-width: min(1100px, 94vw);
  max-height: 80vh;
  object-fit: contain;
  border-radius: 10px;
  cursor: default;
`;

const BigCaption = styled.div`
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-weight: 700;
  color: #cfe0ea;
`;

const OverlayButton = styled.button`
  all: unset;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  cursor: pointer;

  &:hover { background: rgba(255, 255, 255, 0.22); }
  &:focus-visible { outline: 2px solid #1BA8F1; outline-offset: 2px; }
`;

const CloseButton = styled(OverlayButton)`
  position: absolute;
  top: 16px;
  right: 16px;
`;

const NavButton = styled(OverlayButton)`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${({ $side }) => ($side === 'left' ? 'left: 12px;' : 'right: 12px;')}
`;

/**
 * Full-screen view of one capture, with the other reachable from it.
 *
 * Portalled to <body> on purpose: the popup this opens from is itself a
 * scrolling, stacked container, and a fixed overlay nested inside it inherits
 * that clipping the moment any ancestor gains a transform.
 */
const Lightbox = ({ shots, index, onClose, onStep }) => {
  const closeRef = useRef(null);

  // Read the callbacks through a ref so the effect below can run on mount only.
  // The quote polls while this is open, so the parent re-renders every couple of
  // seconds; an effect that depended on the callbacks would re-run each time and
  // yank focus back to the close button mid-scroll.
  const handlers = useRef({ onClose, onStep });
  handlers.current = { onClose, onStep };

  useEffect(() => {
    closeRef.current?.focus();

    const onKey = (event) => {
      if (event.key === 'Escape') handlers.current.onClose();
      else if (event.key === 'ArrowRight') handlers.current.onStep(1);
      else if (event.key === 'ArrowLeft') handlers.current.onStep(-1);
    };
    window.addEventListener('keydown', onKey);

    // The quote behind this scrolls a long way; letting it move under the
    // overlay loses the customer's place in it.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  const shot = shots[index];
  if (!shot) return null;

  return createPortal(
    <Backdrop onClick={onClose} role="dialog" aria-modal="true" aria-label={shot.label}>
      <CloseButton ref={closeRef} onClick={onClose} aria-label="Close image">
        <CloseIcon fontSize="small" />
      </CloseButton>

      {shots.length > 1 && (
        <NavButton
          $side="left"
          aria-label="Previous image"
          onClick={(event) => { event.stopPropagation(); onStep(-1); }}
        >
          <ChevronLeftIcon />
        </NavButton>
      )}

      {/* Stop propagation so tapping the image itself doesn't dismiss it —
          only the backdrop around it closes. */}
      <BigShot src={shot.src} alt={shot.alt} onClick={(event) => event.stopPropagation()} />
      <BigCaption>{shot.label}</BigCaption>

      {shots.length > 1 && (
        <NavButton
          $side="right"
          aria-label="Next image"
          onClick={(event) => { event.stopPropagation(); onStep(1); }}
        >
          <ChevronRightIcon />
        </NavButton>
      )}
    </Backdrop>,
    document.body,
  );
};

/* One status line under both frames, not a caption per image. */
const StatusLine = styled.div`
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  margin-top: 2px;
  font-size: 12.5px;
  color: #6B8090;
  animation: statusPulse 1.9s ease-in-out infinite;

  @keyframes statusPulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.55; }
  }
  @media (prefers-reduced-motion: reduce) { animation: none; }
`;

const Dots = styled.span`
  display: inline-flex;
  gap: 3px;
  i {
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: #1BA8F1;
    display: block;
    animation: dotBounce 1.1s ease-in-out infinite;
  }
  i:nth-child(2) { animation-delay: 0.15s; }
  i:nth-child(3) { animation-delay: 0.3s; }

  @keyframes dotBounce {
    0%, 80%, 100% { transform: translateY(0); opacity: 0.45; }
    40% { transform: translateY(-3px); opacity: 1; }
  }
  @media (prefers-reduced-motion: reduce) { i { animation: none; } }
`;

/**
 * The design render and the sun map, in two fixed frames.
 *
 * Replaces the carousel: the captures upload one at a time and land after the
 * design is already on screen, so a carousel's slide count changed underneath
 * the customer and its loop reset as slides appeared — and it hid content
 * behind a swipe most people never make. Fixed frames reserve their space and
 * simply fill in. The rotated angles are no longer captured at all.
 */
export const DesignShots = ({ designImage, sunMapImage }) => {
  // Both frames are always rendered. Their captures land seconds apart, so
  // reserving both slots up front means the layout never shifts — a frame just
  // stops shimmering. Only real Aurora renders are shown here: the satellite
  // photo is not a design, and standing it in made a bare roof look like the
  // finished system.
  const pending = !designImage || !sunMapImage;

  // Labelled deliberately: unlabelled, a bare heat map reads as a rendering
  // error rather than evidence of a real survey. Normally an irradiance map;
  // the bot falls back to the LIDAR overlay when Aurora cannot compute one in
  // time, and the caption fits both.
  const shots = [
    designImage && { key: 'design', src: designImage, label: 'Your design', alt: 'Your solar design' },
    sunMapImage && {
      key: 'sun',
      src: sunMapImage,
      label: 'Sun exposure',
      alt: 'Sun exposure analysis of your roof',
    },
  ].filter(Boolean);

  // Tracked by key, not by position: the two captures land seconds apart, so an
  // index would quietly point at the other image the moment the second one
  // arrives while the first is open.
  const [expandedKey, setExpandedKey] = useState(null);
  const expandedIndex = shots.findIndex((shot) => shot.key === expandedKey);

  const close = useCallback(() => setExpandedKey(null), []);
  const step = useCallback(
    (direction) =>
      setExpandedKey((current) => {
        const from = shots.findIndex((shot) => shot.key === current);
        if (from < 0) return current;
        return shots[(from + direction + shots.length) % shots.length].key;
      }),
    [shots],
  );

  const renderFrame = (shot, placeholderDelay) => (
    <Frame>
      {shot ? (
        <ZoomTarget
          type="button"
          onClick={() => setExpandedKey(shot.key)}
          aria-label={`Expand ${shot.label.toLowerCase()}`}
        >
          <Shot src={shot.src} alt={shot.alt} />
          <ZoomHint aria-hidden="true"><OpenInFullIcon /></ZoomHint>
        </ZoomTarget>
      ) : (
        <Shimmer $delay={placeholderDelay} />
      )}
    </Frame>
  );

  return (
    <ShotsGrid>
      <Figure>
        {renderFrame(shots.find((shot) => shot.key === 'design'))}
        {designImage && <Caption>Your design</Caption>}
      </Figure>

      <Figure>
        {renderFrame(shots.find((shot) => shot.key === 'sun'), '0.4s')}
        {sunMapImage && <Caption>Sun exposure</Caption>}
      </Figure>

      {pending && (
        <StatusLine>
          <span>Rendering your design</span>
          <Dots><i /><i /><i /></Dots>
        </StatusLine>
      )}

      {expandedIndex >= 0 && (
        <Lightbox shots={shots} index={expandedIndex} onClose={close} onStep={step} />
      )}
    </ShotsGrid>
  );
};

/* ------------------------------------------------------------- stat chips */

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  justify-content: center;
  width: 100%;
`;

const Chip = styled.div`
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  background: #f6fafc;
  border: 1px solid #e0e6ea;
  border-radius: 999px;
  padding: 5px 12px;
  font-size: 12.5px;
  white-space: nowrap;
  b { font-size: 13.5px; color: #0F1B24; font-variant-numeric: tabular-nums; }
  span { color: #808080; }
`;

/**
 * System size, production and offset as chips.
 *
 * Previously a second three-column stat block at the same weight as the
 * payment. They're reassurance once the customer has decided, not the decision
 * itself, so they no longer compete for the eye.
 */
export const SystemStats = ({ systemSize, annualProduction, offset }) => (
  <ChipRow>
    {systemSize > 0 && (
      <Chip><b>{(systemSize / 1000).toFixed(1)} kW</b><span>system</span></Chip>
    )}
    {annualProduction > 0 && (
      <Chip><b>{Math.round(annualProduction).toLocaleString()}</b><span>kWh / year</span></Chip>
    )}
    {offset > 0 && (
      <Chip><b>{Math.round(offset)}%</b><span>of your usage</span></Chip>
    )}
  </ChipRow>
);

/* ------------------------------------------------------- payment vs. bill */

const HeroBox = styled.div`
  background: #f6fafc;
  border: 1px solid #e0e6ea;
  border-radius: 10px;
  padding: 16px;
  width: 100%;
`;

const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 12px;
  align-items: center;
  @media (max-width: 620px) {
    grid-template-columns: 1fr;
  }
`;

const Amount = styled.div`
  font-size: ${({ $primary }) => ($primary ? '34px' : '24px')};
  font-weight: ${({ $primary }) => ($primary ? 800 : 700)};
  letter-spacing: -0.03em;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  color: ${({ $primary }) => ($primary ? '#0F1B24' : '#808080')};
  span { font-size: 0.5em; font-weight: 700; }
`;

const AmountCap = styled.div`
  font-size: 12px;
  color: #808080;
  margin-top: 5px;
`;

const Vs = styled.div`
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  font-weight: 700;
  color: #a8b4bc;
  @media (max-width: 620px) { display: none; }
`;

const Delta = styled.div`
  margin-top: 14px;
  padding-top: 13px;
  border-top: 1px solid #e0e6ea;
  display: flex;
  align-items: center;
  gap: 9px;
  flex-wrap: wrap;
  font-size: 13.5px;
`;

const Pill = styled.span`
  font-weight: 800;
  font-size: 13px;
  padding: 3px 10px;
  border-radius: 999px;
  font-variant-numeric: tabular-nums;
  background: ${({ $cheaper }) => ($cheaper ? 'rgba(14,159,110,.14)' : 'rgba(180,83,9,.14)')};
  color: ${({ $cheaper }) => ($cheaper ? '#0E9F6E' : '#B45309')};
`;

const Why = styled.span`
  color: #3C4E5A;
`;

/**
 * The monthly payment set against the bill the customer already pays.
 *
 * The delta follows the sign of the difference. A blanket "save from day one"
 * would be false on battery-heavy quotes — a real example in the database pays
 * $507/mo against a $275 bill because three Powerwalls are more than half the
 * system cost — so the wording and colour change rather than overclaiming.
 */
export const PaymentCompare = ({ monthlyPayment, currentBill, batteryQuantity, batteryName }) => {
  const payment = Number(monthlyPayment || 0);
  const bill = Number(currentBill || 0);

  // With no bill on file there is nothing honest to compare against.
  if (!bill) {
    return (
      <HeroBox>
        <Amount $primary>
          ${payment.toFixed(0)}<span>/mo</span>
        </Amount>
        <AmountCap>Your solar payment</AmountCap>
      </HeroBox>
    );
  }

  const diff = payment - bill;
  const cheaper = diff < 0;
  const magnitude = Math.abs(Math.round(diff));

  const why = cheaper
    ? 'less than your current bill, starting month one'
    : batteryQuantity > 0
      ? `and you own the system, plus ${batteryQuantity}× ${batteryName || 'battery'} for backup`
      : 'and you own the system instead of renting power';

  return (
    <HeroBox>
      <HeroGrid>
        <div>
          <Amount $primary>
            ${payment.toFixed(0)}<span>/mo</span>
          </Amount>
          <AmountCap>Your solar payment</AmountCap>
        </div>
        <Vs>vs</Vs>
        <div>
          <Amount>
            ${bill.toFixed(0)}<span>/mo</span>
          </Amount>
          <AmountCap>Your current bill</AmountCap>
        </div>
      </HeroGrid>
      <Delta>
        <Pill $cheaper={cheaper}>
          {cheaper ? '−' : '+'}${magnitude}/mo
        </Pill>
        <Why>{why}</Why>
      </Delta>
    </HeroBox>
  );
};

/* --------------------------------------------------------- payment toggle */

const Segmented = styled.div`
  display: flex;
  border: 1px solid #e0e6ea;
  border-radius: 999px;
  overflow: hidden;
  background: #fff;
  width: 100%;
  max-width: 260px;
  margin: 0 auto;
`;

const SegButton = styled.button`
  flex: 1;
  border: 0;
  background: ${({ $active }) => ($active ? '#1BA8F1' : 'transparent')};
  color: ${({ $active }) => ($active ? '#fff' : '#6B8090')};
  font-weight: ${({ $active }) => ($active ? 800 : 600)};
  font-size: 13.5px;
  padding: 9px 0;
  cursor: pointer;
  font-family: inherit;
  transition: background 160ms ease, color 160ms ease;

  &:hover:not(:disabled) { color: ${({ $active }) => ($active ? '#fff' : '#0B7FBD')}; }
  &:focus-visible { outline: 2px solid #1BA8F1; outline-offset: -3px; }
  &:disabled { cursor: default; }
`;

/**
 * Cash / Finance as a segmented pill.
 *
 * Replaces the MUI ToggleButtonGroup, whose default chrome read as two separate
 * buttons rather than one either/or control. Same contract as before:
 * `onChange(event, newValue)`, ignoring a null value.
 */
export const PaymentToggle = ({ value, onChange, options = ['Cash', 'Finance'], disabled }) => (
  <Segmented role="group" aria-label="Payment type">
    {options.map((opt) => (
      <SegButton
        key={opt}
        type="button"
        $active={value === opt}
        aria-pressed={value === opt}
        disabled={disabled}
        onClick={(e) => onChange(e, opt)}
      >
        {opt}
      </SegButton>
    ))}
  </Segmented>
);

/* -------------------------------------------------------------- promotion */

const PromoBar = styled.div`
  display: flex;
  align-items: center;
  gap: 11px;
  flex-wrap: wrap;
  border: 1px solid #f3d9a8;
  border-radius: 10px;
  background: #fff4e0;
  padding: 10px 13px;
  margin-bottom: 12px;
`;

const PromoTag = styled.span`
  background: #ffa300;
  color: #20160a;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  border-radius: 5px;
  padding: 4px 9px;
  white-space: nowrap;
`;

const PromoText = styled.span`
  font-size: 13px;
  color: #6b4a12;

  b {
    color: #3d2a08;
  }
`;

const PromoAmount = styled.span`
  margin-left: auto;
  font-size: 14px;
  font-weight: 800;
  color: #b06a00;
  font-variant-numeric: tabular-nums;
`;

/**
 * States the promotion the customer earned, above whichever payment view they
 * are looking at.
 *
 * Both views already price off the discounted figure, but a lower number with
 * no explanation reads as the price simply being that — and the discount is the
 * reason they picked this hardware. Renders nothing when none applies rather
 * than showing an empty slot.
 */
export const PromotionBanner = ({ hardware }) => {
  const amount = Number(hardware?.promotionAmount) || 0;
  const promotion = hardware?.promotion;
  if (!(amount > 0) || !promotion) return null;

  return (
    <PromoBar>
      <PromoTag>{promotion.headline}</PromoTag>
      <PromoText>
        <b>{promotion.name}</b> promotion applied
      </PromoText>
      <PromoAmount>−{money(amount)}</PromoAmount>
    </PromoBar>
  );
};

/* ------------------------------------------------------------ cash summary */

/**
 * The cash equivalent of PaymentCompare, so the two views read as one design.
 *
 * Finance leads with a monthly payment against the monthly bill; cash leads with
 * the after-credit price against the yearly electricity spend it displaces.
 */
export const CashSummary = ({ hardware, currentBill }) => {
  const afterPromotion = Number(hardware?.totalAfterPromotion || 0);
  const promotion = Number(hardware?.promotionAmount) > 0 ? hardware.promotion : null;
  const yearlyBill = Number(currentBill || 0) * 12;

  return (
    <HeroBox>
      <Amount $primary>${afterPromotion.toLocaleString('en-US', { maximumFractionDigits: 0 })}</Amount>
      <AmountCap>
        {promotion ? `Your price with ${promotion.headline}` : 'Your price'}
      </AmountCap>
      {yearlyBill > 0 && (
        <Delta>
          <Pill $cheaper>
            ~${Math.round(yearlyBill).toLocaleString()}/yr
          </Pill>
          <Why>is what you currently spend on electricity</Why>
        </Delta>
      )}
    </HeroBox>
  );
};

/* -------------------------------------------------------------- disclosures */

const DisclosureText = styled.div`
  font-size: 11.5px;
  line-height: 1.55;
  color: #6B8090;
  p { margin: 0 0 9px; }
  p:last-child { margin-bottom: 0; }
`;

/**
 * Terms, folded away.
 *
 * These ran to two full paragraphs of body copy directly above the button —
 * enough to push the call to action off screen and long enough that nobody read
 * them. Same words, one click away.
 */
export const Disclosures = ({ mode }) => (
  <Disclosure>
    <summary>Important details &amp; authorization</summary>
    <DetailInner>
      <DisclosureText>
        {/* These used to point customers at a tax professional, because the
            price was quoted after a 30% federal tax credit they had to claim
            themselves. Promotions come off the price here, so what matters now
            is that the promotion depends on the equipment staying as
            configured. */}
        {mode === 'Finance' ? (
          <p>
            The estimated payment may vary until your credit application is approved.
            Installation costs are included, though additional upgrades, such as electrical panel
            work or hidden conduit, may be required. Any promotion shown applies to the equipment as
            configured here; changing the panels, inverter or battery may change the price, and the
            Participate promotional pricing is only available paying cash or financing through the
            lender shown. Promotions are limited-time offers and may end without notice.
          </p>
        ) : (
          <p>
            Installation fees are covered, but some additional work may be needed, such as upgrading
            the electrical main panel or installing hidden conduit, and Lunex Direct cannot guarantee
            those costs. Any promotion shown applies to the equipment as configured here; changing
            the panels, inverter or battery may change the price. Promotions are limited-time offers
            and may end without notice.
          </p>
        )}
        <p>
          I authorize Lunex Direct to reach out to me regarding this inquiry, as well as to provide
          additional information about Lunex products and services using the contact details I've
          provided. Standard message and data rates may apply.
        </p>
      </DisclosureText>
    </DetailInner>
  </Disclosure>
);

/* ------------------------------------------------------------ confirm step */

const ConfirmBox = styled.div`
  border: 1px solid #e0e6ea;
  border-radius: 10px;
  background: #f6fafc;
  padding: 16px;
  width: 100%;
  text-align: center;
`;

const ConfirmLead = styled.div`
  font-size: 14.5px;
  font-weight: 700;
  color: #0F1B24;
  margin-bottom: 4px;
`;

const ConfirmSub = styled.div`
  font-size: 13px;
  color: #6B8090;
`;

const ConfirmEmail = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #0B7FBD;
  margin-top: 9px;
  word-break: break-word;
`;

const ChosenRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  align-items: baseline;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e0e6ea;
  font-size: 13px;
  color: #3C4E5A;
  b { color: #0F1B24; font-variant-numeric: tabular-nums; }
`;

/**
 * Shown once the customer has picked a payment type.
 *
 * Previously three loose lines of centred text. Restating what they chose next
 * to where it's going makes the last step feel like a confirmation rather than
 * an unexplained form.
 */
export const ConfirmStep = ({ email, mode, monthlyPayment, cashTotal, designErrored }) => (
  <ConfirmBox>
    <ConfirmLead>
      {designErrored ? "We'll take it from here" : 'Ready when you are'}
    </ConfirmLead>
    <ConfirmSub>
      {designErrored
        ? 'Submit and a solar specialist will reach out to finalize your design.'
        : 'Submit and we\'ll email your contract to get started.'}
    </ConfirmSub>
    <ConfirmEmail>{email}</ConfirmEmail>
    <ChosenRow>
      <span>{mode === 'Finance' ? 'Financing' : 'Paying cash'}</span>
      {mode === 'Finance' && monthlyPayment ? (
        <b>${Number(monthlyPayment).toFixed(0)}/mo</b>
      ) : cashTotal ? (
        <b>${Number(cashTotal).toLocaleString('en-US', { maximumFractionDigits: 0 })}</b>
      ) : null}
    </ChosenRow>
  </ConfirmBox>
);

/* ------------------------------------------------------------ notices */

const NoticeBox = styled.div`
  width: 100%;
  max-width: 420px;
  margin: 0 auto;
  display: flex;
  gap: 11px;
  align-items: flex-start;
  text-align: left;
  padding: 13px 14px;
  border-radius: 10px;
  border: 1px solid ${({ $tone }) => ($tone === 'warn' ? '#F3D9A8' : '#F2C2C2')};
  background: ${({ $tone }) => ($tone === 'warn' ? '#FDF7EC' : '#FDF1F1')};
  animation: noticeIn 300ms ease both;

  @keyframes noticeIn {
    from { opacity: 0; transform: translateY(-3px); }
    to { opacity: 1; transform: none; }
  }
  @media (prefers-reduced-motion: reduce) { animation: none; }
`;

const NoticeIcon = styled.div`
  flex: 0 0 auto;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 800;
  color: #fff;
  background: ${({ $tone }) => ($tone === 'warn' ? '#B45309' : '#C0392B')};
  margin-top: 1px;
`;

const NoticeBody = styled.div`
  min-width: 0;
  b {
    display: block;
    font-size: 13.5px;
    color: #0F1B24;
    line-height: 1.35;
    margin-bottom: 2px;
  }
  span {
    display: block;
    font-size: 12.5px;
    color: #3C4E5A;
    line-height: 1.5;
  }
`;

const NoticeAction = styled.button`
  margin-top: 9px;
  border: 1px solid ${({ $tone }) => ($tone === 'warn' ? '#B45309' : '#C0392B')};
  background: transparent;
  color: ${({ $tone }) => ($tone === 'warn' ? '#B45309' : '#C0392B')};
  border-radius: 999px;
  padding: 5px 14px;
  font-size: 12.5px;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;

  &:hover { background: ${({ $tone }) => ($tone === 'warn' ? 'rgba(180,83,9,.08)' : 'rgba(192,57,43,.08)')}; }
  &:focus-visible { outline: 2px solid #1BA8F1; outline-offset: 2px; }
`;

/**
 * Inline notice for problems the customer can act on.
 *
 * Replaces a bare 1.5rem line of red text. An error should say what happened,
 * what it means for them, and what to do next — "Something went wrong" does
 * none of those, and at that size it read as a system crash rather than a
 * recoverable hiccup on one request.
 */
export const ErrorNotice = ({ title, detail, tone = 'error', actionLabel, onAction }) => (
  <NoticeBox $tone={tone} role="alert">
    <NoticeIcon $tone={tone} aria-hidden="true">!</NoticeIcon>
    <NoticeBody>
      <b>{title}</b>
      {detail && <span>{detail}</span>}
      {actionLabel && onAction && (
        <NoticeAction type="button" $tone={tone} onClick={onAction}>
          {actionLabel}
        </NoticeAction>
      )}
    </NoticeBody>
  </NoticeBox>
);

/* ------------------------------------------------------------ success view */

const SuccessWrap = styled.div`
  width: 100%;
  max-width: 420px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  animation: successIn 420ms ease both;

  @keyframes successIn {
    from { opacity: 0; transform: translateY(6px); }
    to { opacity: 1; transform: none; }
  }
  @media (prefers-reduced-motion: reduce) { animation: none; }
`;

const Check = styled.div`
  width: 46px;
  height: 46px;
  border-radius: 50%;
  background: #0E9F6E;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  line-height: 1;
  margin-bottom: 12px;
  animation: checkPop 460ms cubic-bezier(.2, .9, .3, 1.2) both;

  @keyframes checkPop {
    from { transform: scale(0.6); opacity: 0; }
    to { transform: scale(1); opacity: 1; }
  }
  @media (prefers-reduced-motion: reduce) { animation: none; }
`;

const SuccessTitle = styled.div`
  font-size: 20px;
  font-weight: 800;
  letter-spacing: -0.01em;
  color: #0F1B24;
`;

const SentTo = styled.div`
  font-size: 13.5px;
  color: #6B8090;
  margin-top: 5px;
  b { color: #0B7FBD; word-break: break-word; }
`;

/* Continuous rail rather than arrow glyphs between boxes: it reads as one
   process instead of three disconnected cards. */
const Timeline = styled.ol`
  list-style: none;
  margin: 20px 0 0;
  padding: 0;
  width: 100%;
  text-align: left;
  position: relative;
`;

const Step = styled.li`
  display: grid;
  grid-template-columns: 26px 1fr;
  gap: 12px;
  padding-bottom: ${({ $last }) => ($last ? '0' : '18px')};
  position: relative;

  /* The rail, drawn only between markers. */
  &::before {
    content: '';
    display: ${({ $last }) => ($last ? 'none' : 'block')};
    position: absolute;
    left: 12px;
    top: 24px;
    bottom: 2px;
    width: 2px;
    background: #e0e6ea;
  }
`;

const Marker = styled.div`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 800;
  z-index: 1;
  background: ${({ $done }) => ($done ? '#0E9F6E' : '#fff')};
  color: ${({ $done }) => ($done ? '#fff' : '#8098A8')};
  border: ${({ $done }) => ($done ? 'none' : '2px solid #e0e6ea')};
`;

const StepBody = styled.div`
  padding-top: 2px;
  b {
    display: block;
    font-size: 14px;
    color: #0F1B24;
    line-height: 1.3;
  }
  span {
    display: block;
    font-size: 12.5px;
    color: #6B8090;
    margin-top: 2px;
  }
`;

const Recap = styled.div`
  margin-top: 20px;
  padding-top: 14px;
  border-top: 1px solid #e0e6ea;
  width: 100%;
  display: flex;
  justify-content: center;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 13px;
  color: #6B8090;
  b { color: #0F1B24; font-variant-numeric: tabular-nums; }
`;

/**
 * Post-submit confirmation.
 *
 * Replaces "You're in! Here's what's gonna happen next" — chirpy for someone
 * who has just committed to tens of thousands of dollars, and it dropped the
 * customer's email, which is the one thing they want to verify at that moment.
 * The steps become a single connected timeline with the first already complete,
 * so the page shows progress rather than a list of promises.
 */
export const SuccessPanel = ({ email, firstName, systemSize, mode, monthlyPayment, cashTotal }) => {
  const kw = systemSize ? (systemSize / 1000).toFixed(1) : null;

  return (
    <SuccessWrap>
      <Check aria-hidden="true">✓</Check>
      <SuccessTitle>
        {firstName ? `You're all set, ${firstName}` : "You're all set"}
      </SuccessTitle>
      {/* Not "your contract is on its way". Submitting sends a deal email to
          the Lunex team and nothing at all to the customer — telling them to
          watch their inbox for an agreement that was never sent is the one
          promise on this screen guaranteed to be broken. */}
      {email && (
        <SentTo>
          We'll be in touch at <b>{email}</b>
        </SentTo>
      )}

      <Timeline>
        <Step>
          <Marker $done aria-hidden="true">✓</Marker>
          <StepBody>
            <b>Your system is booked in</b>
            <span>A Lunex advisor will confirm the details and send your agreement to sign.</span>
          </StepBody>
        </Step>
        <Step>
          <Marker aria-hidden="true">2</Marker>
          <StepBody>
            <b>Site survey</b>
            <span>Once signed, we'll schedule a visit and file for permits.</span>
          </StepBody>
        </Step>
        <Step $last>
          <Marker aria-hidden="true">3</Marker>
          <StepBody>
            <b>Installation</b>
            <span>After permits are approved, we'll book your install date.</span>
          </StepBody>
        </Step>
      </Timeline>

      {(kw || monthlyPayment || cashTotal) && (
        <Recap>
          {kw && <span><b>{kw} kW</b> system</span>}
          {mode === 'Finance' && monthlyPayment ? (
            <span>· <b>${Number(monthlyPayment).toFixed(0)}/mo</b> financed</span>
          ) : cashTotal ? (
            <span>· <b>${Number(cashTotal).toLocaleString('en-US', { maximumFractionDigits: 0 })}</b> cash</span>
          ) : null}
        </Recap>
      )}
    </SuccessWrap>
  );
};

/* ----------------------------------------------------------- system kit */

const KitBox = styled.div`
  border: 1px solid #e0e6ea;
  border-radius: 10px;
  overflow: hidden;
  width: 100%;
`;

const KitHead = styled.div`
  font-size: 10.5px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  font-weight: 700;
  color: #808080;
  padding: 9px 13px 7px;
  background: #f6fafc;
  border-bottom: 1px solid #e0e6ea;
`;

const KitRow = styled.div`
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 10px 13px;
  & + & { border-top: 1px solid #e0e6ea; }
`;

const KitIcon = styled.div`
  flex: 0 0 auto;
  width: 26px;
  height: 26px;
  border-radius: 7px;
  background: ${({ $warm }) => ($warm ? 'rgba(255,165,0,.18)' : 'rgba(27,168,241,.14)')};
`;

const KitMain = styled.div`
  display: flex;
  flex-direction: column;
  line-height: 1.35;
  min-width: 0;
  b { font-size: 13.5px; color: #0F1B24; }
  span { font-size: 12px; color: #808080; }
`;

/** What the customer is actually buying, given its own block. */
export const SystemKit = ({ hardware }) => {
  const watts = hardware?.module?.watts;
  const panelCount = watts ? Math.round(hardware.systemSize / watts) : null;
  const kw = hardware?.systemSize ? (hardware.systemSize / 1000).toFixed(1) : null;
  const hasSolar = Boolean(hardware?.systemSize > 0);
  const batteryQty = hardware?.batteryQuantity || 0;
  const serviceQty = hardware?.serviceQuantity || 0;

  return (
    <KitBox>
      <KitHead>What you're getting</KitHead>

      {hasSolar && (
        <KitRow>
          <KitIcon />
          <KitMain>
            <b>{panelCount ? `${panelCount} solar panels` : 'Solar panels'}</b>
            <span>
              {hardware?.module?.name}
              {watts ? ` ${watts} W` : ''}
              {kw ? ` · ${kw} kW system` : ''}
            </span>
          </KitMain>
        </KitRow>
      )}

      {hasSolar && hardware?.inverter?.name && (
        <KitRow>
          <KitIcon />
          <KitMain>
            <b>{hardware.inverter.name}</b>
            <span>
              {hardware.inverter?.column1?.Type}
              {hardware.inverter?.column2?.Warranty ? ` · ${hardware.inverter.column2.Warranty} warranty` : ''}
            </span>
          </KitMain>
        </KitRow>
      )}

      {batteryQty > 0 && (
        <KitRow>
          <KitIcon $warm />
          <KitMain>
            <b>{batteryQty}× {hardware?.battery?.name}</b>
            <span>
              {hardware?.backupHours
                ? `About ${Math.round(hardware.backupHours)} hours of backup power`
                : 'Backup power during outages'}
            </span>
          </KitMain>
        </KitRow>
      )}

      {serviceQty > 0 && (
        <KitRow>
          <KitIcon />
          <KitMain>
            <b>{serviceQty}× {hardware?.services?.name}</b>
            <span>Installed with your system</span>
          </KitMain>
        </KitRow>
      )}
    </KitBox>
  );
};

/* ------------------------------------------------------ cost breakdown */

const SummaryBox = styled.div`
  border: 1px solid #e0e6ea;
  border-radius: 10px;
  overflow: hidden;
  width: 100%;
`;

const SumRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 9px 13px;
  font-size: 13.5px;
  font-variant-numeric: tabular-nums;
  color: #3C4E5A;
  & + & { border-top: 1px solid #e0e6ea; }
  ${({ $total }) => $total && `background:#f6fafc;font-weight:800;color:#0F1B24;`}
  ${({ $credit }) => $credit && `span:last-child{color:#0E9F6E;}`}
`;

const Disclosure = styled.details`
  border: 1px solid #e0e6ea;
  border-radius: 10px;
  width: 100%;
  summary {
    padding: 9px 13px;
    font-size: 13px;
    cursor: pointer;
    color: #0B7FBD;
    font-weight: 600;
  }
  summary:focus-visible { outline: 2px solid #1BA8F1; outline-offset: 2px; }
`;

const DetailInner = styled.div`
  padding: 0 13px 12px;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 12.5px;
  padding: 5px 0;
  font-variant-numeric: tabular-nums;
  color: #3C4E5A;
  ${({ $sub }) => $sub && `font-weight:700;color:#0F1B24;border-top:1px solid #e0e6ea;`}
  ${({ $credit }) => $credit && `span:last-child{color:#0E9F6E;}`}
`;

/** Headline arithmetic always visible; the itemisation one click away. */
export const CostBreakdown = ({ hardware, monthlyPayment }) => {
  const solar = Number(hardware?.solarCost || 0);
  const battery = Number(hardware?.batteryCost || 0);
  const service = Number(hardware?.serviceCost || 0);
  const total = Number(hardware?.totalCost || 0);
  const discount = Number(hardware?.promotionAmount || 0);
  const promotion = discount > 0 ? hardware.promotion : null;
  const afterPromotion = Number(hardware?.totalAfterPromotion || total);
  const down = Number(hardware?.downPayment || 0);
  const financed = Number(hardware?.financedTotal || afterPromotion);
  const watts = hardware?.module?.watts;
  const panelCount = watts ? Math.round(hardware.systemSize / watts) : null;

  return (
    <>
      <SummaryBox>
        {promotion ? (
          <>
            <SumRow><span>System total</span><span>{money(total)}</span></SumRow>
            <SumRow $credit>
              <span>{promotion.label} — {promotion.headline}</span>
              <span>−{money(discount)}</span>
            </SumRow>
            <SumRow $total><span>Your price</span><span>{money(afterPromotion)}</span></SumRow>
          </>
        ) : (
          <SumRow $total><span>Your price</span><span>{money(total)}</span></SumRow>
        )}
      </SummaryBox>

      <Disclosure>
        <summary>See full cost breakdown</summary>
        <DetailInner>
          {solar > 0 && (
            <DetailRow>
              <span>
                Solar{panelCount ? ` — ${panelCount} panels` : ''}
                {hardware?.solarPPW ? ` @ $${Number(hardware.solarPPW).toFixed(2)}/W` : ''}
              </span>
              <span>{money(solar)}</span>
            </DetailRow>
          )}
          {battery > 0 && (
            <DetailRow>
              <span>Battery — {hardware?.batteryQuantity}× {hardware?.battery?.name}</span>
              <span>{money(battery)}</span>
            </DetailRow>
          )}
          {service > 0 && (
            <DetailRow>
              <span>{hardware?.services?.name}</span>
              <span>{money(service)}</span>
            </DetailRow>
          )}
          <DetailRow $sub><span>System total</span><span>{money(total)}</span></DetailRow>
          {promotion && (
            <DetailRow $credit>
              <span>{promotion.label} — {promotion.headline}</span>
              <span>−{money(discount)}</span>
            </DetailRow>
          )}
          <DetailRow $sub><span>Your price</span><span>{money(afterPromotion)}</span></DetailRow>
          <DetailRow><span>Down payment</span><span>{money(down)}</span></DetailRow>
          <DetailRow $sub><span>Amount financed</span><span>{money(financed)}</span></DetailRow>
          {monthlyPayment ? (
            <DetailRow>
              <span>
                {hardware?.selectedProduct?.term ? `${Math.floor(hardware.selectedProduct.term)} years` : 'Term'}
                {/* A payment-factor loan has no APR to quote. */}
                {!hardware?.selectedProduct?.factor && hardware?.selectedProduct?.apr
                  ? ` @ ${hardware.selectedProduct.apr}% APR`
                  : ''}
                {hardware?.selectedLender ? ` · ${hardware.selectedLender}` : ''}
              </span>
              <span>${Number(monthlyPayment).toFixed(0)}/mo</span>
            </DetailRow>
          ) : null}
        </DetailInner>
      </Disclosure>
    </>
  );
};
